import { getPriceString, getRewardText } from "halifax";
import {
  ExperiencePricingCategory,
  ExperiencePricingCategoryEnum,
  ExperiencesAgeBandEnum,
  ExperiencesPriceBreakdown,
  Prices,
  RewardsAccount,
} from "redmond";
import { ParsedAgeBand } from "./reducer";

export const BACK_TO_RESULTS = "Back to results";

// common shared
export const CONTINUE_TO_CHECKOUT = "Continue to checkout";
export const VIEW_DETAILS = "View details";
export const HIDE_DETAILS = "Hide details";
export const PER_GROUP = "per group";
export const PER_ADULT = "per adult";

export const WHAT_TO_EXPECT = "What to expect";
export const WHATS_INCLUDED = "What's included";
export const CUSTOMER_REVIEWS = "Customer reviews";
export const TRAVELERS_PHOTOS = "Traveler Photos";
export const OTHER_SIMILAR_EXPERIENCES = "Other similar activities";
export const COMPARE_OTHER_SIMILAR_EXPERIENCES =
  "Compare other similar activities";

export const SHOP_DETAIL_HEADER_TEXT = (
  experienceName: string,
  locationName: string
) => `${experienceName} in ${locationName}`;

export const PARSE_TO_MINUTES = (minutes: number) => `${minutes} minutes`;

export const AVAILABILITY = "Availability";
export const AVAILABILITY_EXCEEDS_DATE_RANGE =
  "Some of your searched dates are unavailable, below are the following available dates.";
export const PASS_BY = " (Pass by)";
export const READ_MORE = "Read more";
export const READ_LESS = "Read less";

export const END_SAME_AS_START = "This activity ends at the starting point.";
export const VIEW_ON_MAP = "View on map";
export const STARTING_AND_END_POINT = "Starting & end point";
export const VIEW_ALL = "View all";
export const CONFIRM_TRAVELERS = "Confirm travelers";
export const CONFIRM_TRAVELERS_WITH_START_TIME =
  "Confirm travelers and start time";
export const SHOW_LESS = "Show less";
export const STARTING_POINT = "Starting point";
export const END_POINT = "End point";
export const PRICE_PER_GROUP = "Prices shown are per group";
export const PRICE_PER_ADULT = "Prices shown are per adult";
export const SELECT_DATE = "Select date";
export const CHOOSE_START_TIME = "Choose start time";

// photo gallery
export const SHOW_ALL_PHOTOS_BUTTON_LABEL = (numPhotos: number) =>
  `See all photos (${numPhotos})`;
export const BOOK_NOW = "Book now";
export const ALL_PHOTOS = "All photos";
export const REVIEWS_POWERED_BY = "Reviews powered by";
export const TRIPADVISOR = "TripAdvisor";
export const PROVIDER_PHOTOS = "Provider photos";
export const TRAVELER_PHOTOS = "Traveler photos";

// shop similar activities
export const RATING = "Rating";
export const DURATION = "Duration";
export const CANCELLATION = "Cancellation";
export const PRICE = "Price";
export const NON_REFUNDABLE = "Non-refundable";
export const CURRENT = "Current";
export const AVG_REVIEWS = ({
  avg,
  numberOfReviews,
}: {
  avg: number;
  numberOfReviews: number;
}) => `${avg} avg (${numberOfReviews} reviews)`;
export const COMPARE_ITINERARY = "Compare itinerary";
export const SEE_MORE_SIMILAR_EXPERIENCES = "See more similar activities";

// travelers
export const EDIT_TRAVELERS = "Edit travelers";
export const ADULTS = "Adults";
export const CHILDREN = "Children";
export const SENIORS = "Seniors";
export const YOUTHS = "Youths";
export const INFANTS = "Infants";
export const GROUP = "group";
export const PERSON = "person";
export const ADULT_REQUIRED = "At least 1 adult is required";
export const AGES_SUBTITLE = (lowerBound?: number, upperBound?: number) => {
  if (lowerBound === undefined || upperBound === undefined) {
    return "";
  }

  return `Ages ${lowerBound} - ${upperBound}`;
};

// checkout breakdown
export const PRICE_TITLE = (
  price: Prices,
  pricingCategory?: ExperiencePricingCategory
) => {
  const formattedPrice = getPriceString({
    price: price.fiat.value,
    currencySymbol: price.fiat.currencySymbol,
  });

  const priceUnit =
    pricingCategory?.ExperiencePricingCategory ===
    ExperiencePricingCategoryEnum.PerGroup
      ? PER_GROUP
      : PER_ADULT;

  return `${formattedPrice} ${priceUnit}`;
};

export const CART_UPDATE_TEXT = "Fetching latest price";

export const NO_PRICE_BREAKDOWN_TITLE = "No price found";
export const NO_PRICE_BREAKDOWN_SUBTITLE = "Please select a different date.";

export const getPriceRewardTitles = (
  priceBreakdown?: ExperiencesPriceBreakdown,
  parsedAgeBands?: { [key in ExperiencesAgeBandEnum]?: ParsedAgeBand },
  pricingCategory?: ExperiencePricingCategory,
  largestValueAccount?: RewardsAccount
) => {
  let priceTitle = "";
  let rewardsTitle = "";

  if (
    pricingCategory?.ExperiencePricingCategory ===
      ExperiencePricingCategoryEnum.PerGroup &&
    parsedAgeBands?.Traveler?.priceByCount
  ) {
    const price = parsedAgeBands.Traveler.priceByCount(
      parsedAgeBands.Traveler.minNumberOfTravelers ?? 1
    );
    if (price) {
      priceTitle = PRICE_TITLE(price, pricingCategory);
    }
  } else if (priceBreakdown) {
    const ageBand =
      pricingCategory?.ExperiencePricingCategory ===
      ExperiencePricingCategoryEnum.PerGroup
        ? ExperiencesAgeBandEnum.TRAVELER
        : ExperiencesAgeBandEnum.ADULT;
    const chosenLineItem =
      priceBreakdown.lineItems.find(
        (item) => item.specificAgeBand.ageBand === ageBand
      ) || priceBreakdown.lineItems[0];

    priceTitle = PRICE_TITLE(chosenLineItem.specificAgeBand.pricePerTraveler);
    rewardsTitle = largestValueAccount
      ? `${getRewardText({
          reward:
            chosenLineItem.specificAgeBand.pricePerTraveler.rewards[
              largestValueAccount.accountReferenceId
            ],
        })}`
      : "";
  } else if (parsedAgeBands && parsedAgeBands.Adult?.priceByCount) {
    const price = parsedAgeBands.Adult.priceByCount(
      parsedAgeBands.Adult.minNumberOfTravelers ?? 1
    );
    if (price) {
      priceTitle = PRICE_TITLE(price);
      rewardsTitle = largestValueAccount
        ? `${getRewardText({
            reward: price.rewards[largestValueAccount.accountReferenceId],
          })}`
        : "";
    }
  }
  return {
    priceTitle,
    rewardsTitle,
  };
};

export const MAX_TRAVELER_TEXT = (travelerCount: number) =>
  `Up to ${travelerCount} ${travelerCount === 1 ? "person" : "people"}`;

export const getPriceAndRewardsCopy = ({
  price,
  rewards,
  separator,
}: {
  price: string;
  rewards?: string;
  separator?: string;
}) =>
  `<strong>${price}</strong>${
    rewards ? ` ${separator ?? "/"} ${rewards}` : ""
  }`;

export const PRICE_VARIES_ON_GROUP_SIZE =
  "Price varies depending on group size";

export const makePriceString = (ageBand: ParsedAgeBand) => (count: number) => {
  const maybePrice = ageBand?.priceByCount?.(count);
  if (maybePrice && !ageBand?.priceVariesByCount) {
    return `${getPriceString({
      price: maybePrice.fiat.value,
    })}`;
  }

  return "";
};

// reviews
export const SEARCH_BY_KEYWORD = "Search reviews by keyword";
export const NO_REVIEWS = "No reviews";
export const NO_MATCHING_REVIEWS = "No matching reviews";
